import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type FocusEvent,
  type MouseEvent,
  type Ref,
} from 'react';
import { DayPicker, getDefaultClassNames, type DayButton, type DropdownProps, type Locale } from 'react-day-picker';

import { Button, ButtonVariant, buttonVariants } from '@ui/components/base/button';
import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

const CALENDAR_DROPDOWN_MAX_HEIGHT_CLASS = 'max-h-60';
const CALENDAR_CAPTION_LABEL_DROPDOWN_CLASS =
  'flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground';

interface CalendarRootProps extends ComponentProps<'div'> {
  rootRef?: Ref<HTMLDivElement>;
}

const CalendarRoot = ({ className, rootRef, ...props }: CalendarRootProps) => (
  <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
);

interface CalendarChevronProps extends ComponentProps<typeof ChevronDownIcon> {
  orientation?: 'left' | 'right' | 'up' | 'down';
}

const CalendarChevron = ({ className, orientation, ...props }: CalendarChevronProps) => {
  if (orientation === 'left') {
    return <ArrowLeftIcon className={cn('size-4', className)} {...props} />;
  }

  if (orientation === 'right') {
    return <ArrowRightIcon className={cn('size-4', className)} {...props} />;
  }

  return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
};

const CalendarWeekNumber = ({ children, ...props }: ComponentProps<'td'>) => (
  <td {...props}>
    <div className="flex size-(--cell-size) items-center justify-center text-center">{children}</div>
  </td>
);

const CalendarLocaleContext = createContext<Partial<Locale> | undefined>(undefined);

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = ButtonVariant.GHOST,
  locale,
  formatters,
  components,
  ...props
}: ComponentProps<typeof DayPicker> & {
  buttonVariant?: ButtonVariant;
}) => {
  const defaultClassNames = getDefaultClassNames();

  return (
    <CalendarLocaleContext.Provider value={locale}>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn(
          'group/calendar bg-background p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)]',
          'in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
          String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
          String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
          className
        )}
        captionLayout={captionLayout}
        locale={locale}
        formatters={{
          formatMonthDropdown: (date) =>
            date.toLocaleString((locale as { code?: string } | undefined)?.code, { month: 'short' }),
          ...formatters,
        }}
        classNames={{
          root: cn('w-fit', defaultClassNames.root),
          months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
          month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
          nav: cn('absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1', defaultClassNames.nav),
          button_previous: cn(
            buttonVariants({ variant: buttonVariant }),
            'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
            defaultClassNames.button_previous
          ),
          button_next: cn(
            buttonVariants({ variant: buttonVariant }),
            'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
            defaultClassNames.button_next
          ),
          month_caption: cn(
            'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
            defaultClassNames.month_caption
          ),
          dropdowns: cn(
            'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
            defaultClassNames.dropdowns
          ),
          dropdown_root: cn('relative rounded-(--cell-radius)', defaultClassNames.dropdown_root),
          dropdown: cn('absolute inset-0 bg-popover opacity-0', defaultClassNames.dropdown),
          caption_label: cn(
            'font-medium select-none',
            captionLayout === 'label' ? 'text-sm' : CALENDAR_CAPTION_LABEL_DROPDOWN_CLASS,
            defaultClassNames.caption_label
          ),
          weekdays: cn('flex', defaultClassNames.weekdays),
          weekday: cn(
            'flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal text-muted-foreground select-none',
            defaultClassNames.weekday
          ),
          week: cn('mt-2 flex w-full', defaultClassNames.week),
          week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
          week_number: cn('text-[0.8rem] text-muted-foreground select-none', defaultClassNames.week_number),
          day: cn(
            'group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none',
            '[&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)',
            props.showWeekNumber
              ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
              : '[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
            defaultClassNames.day
          ),
          range_start: cn(
            'relative isolate z-0 rounded-l-(--cell-radius) bg-muted',
            'after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted',
            defaultClassNames.range_start
          ),
          range_middle: cn('rounded-none', defaultClassNames.range_middle),
          range_end: cn(
            'relative isolate z-0 rounded-r-(--cell-radius) bg-muted',
            'after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted',
            defaultClassNames.range_end
          ),
          today: cn(
            'rounded-(--cell-radius) bg-muted text-foreground data-[selected=true]:rounded-none',
            defaultClassNames.today
          ),
          outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaultClassNames.outside),
          disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
          hidden: cn('invisible', defaultClassNames.hidden),
          ...classNames,
        }}
        components={{
          Root: CalendarRoot,
          Chevron: CalendarChevron,
          Dropdown: CalendarDropdown,
          DayButton: CalendarDayButton,
          WeekNumber: CalendarWeekNumber,
          ...components,
        }}
        {...props}
      />
    </CalendarLocaleContext.Provider>
  );
};

const CalendarDropdown = ({
  options,
  value,
  onChange,
  disabled,
  className,
  'aria-label': ariaLabel,
}: DropdownProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLButtonElement | null>(null);
  const selectedOption = options?.find((option) => String(option.value) === String(value));

  useEffect(() => {
    if (!open) {
      return;
    }
    selectedOptionRef.current?.scrollIntoView({ block: 'center' });
  }, [open, selectedOption?.value]);

  const handleSelect = (optionValue: number) => {
    onChange?.({ target: { value: String(optionValue) } } as ChangeEvent<HTMLSelectElement>);
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={cn('relative', open && 'z-50', className)}
      onBlur={(event: FocusEvent<HTMLDivElement>) => {
        if (rootRef.current?.contains(event.relatedTarget)) {
          return;
        }
        setOpen(false);
      }}
    >
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        className={cn(
          'flex h-(--cell-size) items-center gap-1 rounded-(--cell-radius) px-2 text-sm font-medium',
          'text-foreground outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50',
          'disabled:pointer-events-none disabled:opacity-50'
        )}
        onClick={() => setOpen((nextOpen) => !nextOpen)}
      >
        {selectedOption?.label}
        <ChevronDownIcon className="size-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div
          id={listId}
          role="listbox"
          className={cn(
            'no-scrollbar absolute top-full left-0 z-50 mt-1 min-w-full overflow-y-auto rounded-md bg-popover p-1',
            'text-popover-foreground shadow-md ring-1 ring-foreground/10',
            CALENDAR_DROPDOWN_MAX_HEIGHT_CLASS
          )}
        >
          {options?.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              ref={option.value === selectedOption?.value ? selectedOptionRef : undefined}
              aria-selected={option.value === selectedOption?.value}
              disabled={option.disabled}
              className={cn(
                'flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                'hover:bg-muted focus:bg-muted disabled:pointer-events-none disabled:opacity-50',
                option.value === selectedOption?.value && 'bg-muted'
              )}
              onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CalendarDayButton = ({ className, day, modifiers, ...props }: ComponentProps<typeof DayButton>) => {
  const defaultClassNames = getDefaultClassNames();
  const locale = useContext(CalendarLocaleContext);

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      variant={ButtonVariant.GHOST}
      size="icon"
      data-day={day.date.toLocaleDateString((locale as { code?: string } | undefined)?.code)}
      data-selected-single={
        modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size)',
        'flex-col gap-1 border-0 leading-none font-normal',
        'group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10',
        'group-data-[focused=true]/day:border-ring',
        'group-data-[focused=true]/day:ring-3 group-data-[focused=true]/day:ring-ring/50',
        'data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius)',
        'data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground',
        'data-[range-middle=true]:rounded-none',
        'data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground',
        'data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius)',
        'data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground',
        'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground',
        'dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
};

export { Calendar, CalendarDayButton };
